import { useEffect, useMemo, useState } from "react";
import { LocalNotifications } from "@capacitor/local-notifications";
import "./PrayerTimes.css";

const PRAYERS = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
const FALLBACK_TIMINGS = { Fajr: "04:52", Dhuhr: "12:28", Asr: "15:54", Maghrib: "18:57", Isha: "20:12" };
const DISPLAY_NAMES = { Fajr: "Fajr", Dhuhr: "Dhuhr", Asr: "Asr", Maghrib: "Maghrib", Isha: "Isha" };
const AUDIO_OPTIONS = ["Makkah", "Madinah", "Dubai"];
const METHODS = [{ id: 16, label: "Dubai (experimental)" }, { id: 8, label: "Gulf Region" }, { id: 4, label: "Umm Al-Qura" }];
const settingsKey = "tarahumPrayerSettings";

const readSettings = () => {
  try { return { azanEnabled: true, reminderEnabled: true, reminderMinutes: 10, audio: "Dubai", method: 16, ...JSON.parse(localStorage.getItem(settingsKey) || "{}") }; }
  catch { return { azanEnabled: true, reminderEnabled: true, reminderMinutes: 10, audio: "Dubai", method: 16 }; }
};
const cleanTime = (value) => String(value || "").match(/\d{1,2}:\d{2}/)?.[0] || "--:--";
const secondsUntil = (time, now = new Date()) => {
  const [hours, minutes] = time.split(":").map(Number);
  const target = new Date(now); target.setHours(hours, minutes, 0, 0);
  if (target <= now) target.setDate(target.getDate() + 1);
  return Math.max(0, Math.floor((target - now) / 1000));
};
const formatCountdown = (seconds) => `${String(Math.floor(seconds / 3600)).padStart(2, "0")}:${String(Math.floor(seconds % 3600 / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

export default function PrayerTimes() {
  const [settings, setSettings] = useState(readSettings);
  const [timings, setTimings] = useState(FALLBACK_TIMINGS);
  const [meta, setMeta] = useState({ date: "Today", hijri: "", source: "Loading live timings…" });
  const [now, setNow] = useState(new Date());
  const [notice, setNotice] = useState("");

  useEffect(() => { localStorage.setItem(settingsKey, JSON.stringify(settings)); }, [settings]);
  useEffect(() => { const timer = window.setInterval(() => setNow(new Date()), 1000); return () => window.clearInterval(timer); }, []);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const date = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Dubai", day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date()).replaceAll("/", "-");
        const response = await fetch(`https://api.aladhan.com/v1/timingsByCity/${date}?city=Dubai&country=AE&method=${settings.method}`, { signal: controller.signal });
        const result = await response.json();
        if (!response.ok || !result?.data?.timings) throw new Error("Prayer times unavailable");
        const next = Object.fromEntries(PRAYERS.map((name) => [name, cleanTime(result.data.timings[name])]));
        setTimings(next);
        setMeta({ date: result.data.date?.readable || date, hijri: result.data.date?.hijri?.date || "", source: result.data.meta?.method?.name || "Aladhan" });
      } catch {
        setMeta((current) => ({ ...current, source: "Offline timings — refresh when connected" }));
      }
    };
    load();
    const refresh = window.setInterval(load, 24 * 60 * 60 * 1000);
    return () => { controller.abort(); window.clearInterval(refresh); };
  }, [settings.method]);

  const nextPrayer = useMemo(() => {
    const values = PRAYERS.map((name) => ({ name, time: timings[name], seconds: secondsUntil(timings[name], now) }));
    return values.sort((a, b) => a.seconds - b.seconds)[0];
  }, [now, timings]);

  const updateSetting = (key, value) => setSettings((current) => ({ ...current, [key]: value }));
  const scheduleNotifications = async () => {
    if (!settings.azanEnabled) { setNotice("Enable Azan first to schedule alerts."); return; }
    try {
      const permission = await LocalNotifications.requestPermissions();
      if (permission.display !== "granted") throw new Error("Notifications permission was not granted.");
      const schedules = PRAYERS.flatMap((name, index) => {
        const [hour, minute] = timings[name].split(":").map(Number);
        const azan = { id: 100 + index, title: `${name} prayer time`, body: `It is time for ${name} in Dubai.`, schedule: { on: { hour, minute }, repeats: true } };
        const reminderMinute = minute - Number(settings.reminderMinutes);
        const reminder = { id: 200 + index, title: `${name} in ${settings.reminderMinutes} minutes`, body: "Prepare for prayer.", schedule: { on: { hour: reminderMinute < 0 ? hour - 1 : hour, minute: (reminderMinute + 60) % 60 }, repeats: true } };
        return settings.reminderEnabled ? [azan, reminder] : [azan];
      });
      await LocalNotifications.cancel({ notifications: schedules.map(({ id }) => ({ id })) });
      await LocalNotifications.schedule({ notifications: schedules });
      setNotice("Daily prayer notifications are scheduled.");
    } catch (error) { setNotice(error.message || "Unable to schedule notifications on this device."); }
  };

  return <main className="prayer-page">
    <section className="prayer-hero">
      <div><span className="prayer-eyebrow">TARAHUM • PRAYER TIMES</span><h1>Dubai Prayer Times</h1><p>{meta.date}{meta.hijri ? ` • ${meta.hijri}` : ""}</p></div>
      <div className="prayer-location"><i className="fa-solid fa-location-dot" /> Dubai, United Arab Emirates</div>
    </section>

    <section className="next-prayer-card">
      <span>Next prayer</span><h2>{DISPLAY_NAMES[nextPrayer?.name] || "Prayer"}</h2><strong>{nextPrayer?.time}</strong><p>Begins in <b>{formatCountdown(nextPrayer?.seconds || 0)}</b></p>
    </section>

    <section className="prayer-list" aria-label="Daily prayer timings">
      {PRAYERS.map((name) => <article key={name} className={name === nextPrayer?.name ? "is-next" : ""}><span className="prayer-icon"><i className="fa-solid fa-mosque" /></span><div><h3>{DISPLAY_NAMES[name]}</h3><p>{name === nextPrayer?.name ? "Next prayer" : "Dubai"}</p></div><time>{timings[name]}</time></article>)}
    </section>

    <section className="prayer-settings">
      <div className="settings-heading"><div><span className="prayer-eyebrow">PERSONALIZE</span><h2>Azan & reminders</h2></div><button type="button" onClick={scheduleNotifications}>Save notifications</button></div>
      <label className="setting-switch"><span><b>Enable Azan</b><small>Receive an alert at each prayer time</small></span><input type="checkbox" checked={settings.azanEnabled} onChange={(event) => updateSetting("azanEnabled", event.target.checked)} /><i /></label>
      <label className="setting-switch"><span><b>Notification before Azan</b><small>Remind me before the prayer begins</small></span><input type="checkbox" checked={settings.reminderEnabled} onChange={(event) => updateSetting("reminderEnabled", event.target.checked)} /><i /></label>
      <div className="setting-selects"><label>Reminder<select value={settings.reminderMinutes} onChange={(event) => updateSetting("reminderMinutes", event.target.value)}>{[5, 10, 15, 20].map((value) => <option key={value} value={value}>{value} minutes before</option>)}</select></label><label>Azan audio<select value={settings.audio} onChange={(event) => updateSetting("audio", event.target.value)}>{AUDIO_OPTIONS.map((value) => <option key={value}>{value}</option>)}</select></label><label>Calculation method<select value={settings.method} onChange={(event) => updateSetting("method", Number(event.target.value))}>{METHODS.map((method) => <option key={method.id} value={method.id}>{method.label}</option>)}</select></label></div>
      {notice ? <p className="prayer-notice">{notice}</p> : null}<p className="prayer-source">Daily automatic update • {meta.source}</p>
    </section>
  </main>;
}
